import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import type { ChangedImagePrompt, ImagePrompt, ImagePromptWithSize } from '@/app/types';
import { checkSourceImageSize, getImageSize } from '@/app/utils';
import { handleChangeImageRange } from '@/app/utils/client';

export const useBatchCrop = (
  maxImageSize: number,
  updateImagePromptByIds: (imagePromptMap: { id: string; prompt: ImagePrompt }[]) => void,
  scene: UploadScene = UPLOAD_SCENE.VIDU,
) => {
  const t = useTranslations();
  const [selectModalImageSource, setSelectModalImageSource] = useState<ImagePromptWithSize | null>(null);

  const handleOpenSelectModal = async (idx: number, imagePrompt?: ImagePrompt | null) => {
    if (!imagePrompt) return;

    if (!checkSourceImageSize(imagePrompt)) {
      try {
        const { width, height } = await getImageSize(imagePrompt.sourceImagePreview);
        imagePrompt.sourceImageWidth = width;
        imagePrompt.sourceImageHeight = height;
      } catch {
        toast({
          type: 'error',
          title: t('create.char2VideoForm.imageError', { index: idx + 1 }),
        });
        return;
      }
    }

    setSelectModalImageSource(imagePrompt as ImagePromptWithSize);
  };

  const handleCloseSelectModal = () => setSelectModalImageSource(null);

  //批量保存裁剪图片
  const handleSaveChangedImagePrompts = async (changedImagePrompts: ChangedImagePrompt[]) => {
    if (changedImagePrompts.length === 0) return;
    try {
      const processedPrompts = await Promise.all(
        changedImagePrompts.map(async ({ id, range, cropRatio, imagePrompt }) => {
          const result = await handleChangeImageRange({
            range,
            imagePrompt,
            maxImageSize,
            scene,
          });
          if (!result) return null;

          // 检查上传错误
          if ('error' in result && result.error) {
            const error = result.error;
            if (error.reason === 'AuditSubmitIllegal') {
              toast({
                type: 'error',
                message: t('create.createForm.upload.error.AuditSubmitIllegal'),
              });
            } else {
              toast({
                type: 'error',
                message: error.message || t('loadImageError'),
              });
            }
            return null;
          }

          // TypeScript 类型守卫：此时 result 一定是 { imagePrompt: ImagePrompt }
          if ('imagePrompt' in result) {
            const newImagePrompt = result.imagePrompt;
            if (cropRatio !== undefined) newImagePrompt.cropRatio = cropRatio;
            return { id, prompt: newImagePrompt };
          }

          return null;
        }),
      );
      const validPrompts = processedPrompts.filter((item) => item !== null);
      if (validPrompts.length > 0) updateImagePromptByIds(validPrompts);
    } catch (error) {
      console.error('缓存图片更新失败:', error);
    }
  };

  return {
    selectModalImageSource,
    handleOpenSelectModal,
    handleCloseSelectModal,
    handleSaveChangedImagePrompts,
  };
};
